/**
 * The one place a generated client function turns into a network request.
 *
 * orval is told to route every endpoint through `apiFetch` (`override.mutator` in
 * `orval.config.ts`), so this file — not the generated code — decides where requests go
 * and how a response is read. It is the choke point later work needs: a session token,
 * a build identifier header and a refresh-on-401 all belong here rather than in three
 * hundred call sites.
 *
 * **Why the base URL is set at runtime.** The API is on a different origin from every
 * client that calls it, and which origin depends on the environment (local, pre-prod,
 * prod) and eventually on the tenant's own domain. Baking one into the generated code
 * would mean a generated artifact per environment, which the committed-artifact check
 * in CI could not keep honest. So the app configures it once at startup and the
 * generated code stays environment-free.
 */

/** Where requests go. Empty means "same origin as the page", which is only ever right on the web. */
let configuredBaseUrl = ''

/** Supplies the credential headers, if the app configured one. See below. */
let configuredHeaders: (() => Readonly<Record<string, string>>) | undefined

/** Called at most once per request when the API answers 401. See below. */
let configuredRecovery: (() => Promise<boolean>) | undefined

/**
 * Called at most once per request when the API answers `build_identifier_revoked` or
 * `build_identifier_invalid`. See `ApiClientConfiguration.recoverFromBuildIdentifierFailure`.
 */
let configuredBuildIdentifierRecovery: (() => Promise<boolean>) | undefined

/** Awaited before every request leaves. See `ApiClientConfiguration.beforeRequest`. */
let configuredBeforeRequest: (() => Promise<void>) | undefined

export type ApiClientConfiguration = {
  /**
   * Origin of the API, with no trailing slash — `https://t4s-api-preprod.lamplighter-site.workers.dev`.
   * An empty string sends requests to the current origin.
   */
  readonly baseUrl: string
  /**
   * Headers merged into every request the client sends — in practice
   * `{ Authorization: 'Bearer …' }`, and eventually `X-Build-Identifier` for the tenant
   * app. It is a function rather than a value because the token changes underneath it:
   * a rotation must not need the client re-configured.
   *
   * A header the caller set explicitly wins, so a one-off request can still override it.
   */
  readonly headers?: () => Readonly<Record<string, string>>
  /**
   * Called when a request answers **401**, to give the app one chance to make the
   * credential good again — a token refresh, in every case this system has. Resolving
   * `true` replays the original request exactly once; `false` (or throwing) leaves the
   * 401 to the caller.
   *
   * Two properties matter more than the hook itself, and both are structural rather
   * than remembered — `docs/reference/ISSUES.md` **CF2** is what the alternative cost:
   * an exclusion list of endpoint names that matched nothing, so a 401 on the refresh
   * call re-entered the refresh path, queued behind itself, and hung every request in
   * the application with no sign-out and no resolution.
   *
   *   - **Concurrent requests share one recovery.** The first 401 starts it; every other
   *     in-flight request awaits the same promise and then replays. One refresh, not
   *     one per request.
   *   - **A request issued while the recovery is running never enters the recovery
   *     path.** That is what keeps the hook's own refresh call out of the layer that
   *     retries on 401: it cannot await the promise it is itself producing. No list of
   *     endpoints is consulted, so none can fall out of step.
   *   - **A request that carried its own `Authorization` header never enters it
   *     either.** The recovery repairs the *configured* credential, which such a
   *     request is deliberately not using; the replay re-sends the same `init`, so it
   *     would present the token that was just refused and be refused identically —
   *     having spent the single-use refresh token on the way past. See `apiFetch`.
   */
  readonly recoverFromUnauthorized?: () => Promise<boolean>
  /**
   * Called when a request answers **400** with `build_identifier_revoked` or
   * `build_identifier_invalid`, to give the tenant app one chance to learn a current
   * identifier before giving up on the build — `docs/DECISIONS.md`, "The branding config
   * file …": revoking a signing key re-mints every build identifier, and with the config
   * file cached for up to a day a naive client would stay broken for up to a day after
   * the key itself was fixed. Resolving `true` replays the original request exactly
   * once, the same as `recoverFromUnauthorized`; `false` (or throwing) leaves the
   * failure to the caller — which for these two codes is `apps/app`'s launch gate and
   * session store, both already built to treat either as "this build is finished"
   * (`isBuildIdentifierFailure`, `apps/app/src/lib/build-identifier.ts`).
   *
   * The same two properties as `recoverFromUnauthorized`, for the same reasons: **one
   * recovery for concurrent failures**, and **a request issued while the recovery is
   * running never enters this path** — the hook's own re-fetch of the config file
   * answers this shape of failure only by definition (a public, unauthenticated GET), so
   * the re-entrancy guard is exercised more by symmetry with the 401 path than by a
   * failure mode this one can actually produce.
   *
   * Deliberately **not** offered `build_identifier_required`: that code means this
   * request carried no identifier at all, which a re-fetch of the *same* config file
   * cannot supply — see `apps/app`'s `isRecoverableBuildIdentifierFailure`.
   */
  readonly recoverFromBuildIdentifierFailure?: () => Promise<boolean>
  /**
   * Awaited before **every** request is sent (a replay included), so the app can hold a
   * request until it has what the request must carry — in practice the tenant app's build
   * identifier, which the shared web bundle learns from `/config.json` after the first
   * render (`fix-redesign-b5-qa`, N1). A request sent before it arrives carries no
   * `X-Build-Identifier`, the API answers `build_identifier_required`, and the app read
   * that as a build that can never work again.
   *
   * It must resolve; a rejection fails the request with that error rather than sending
   * it. It is read when a request *starts* and runs outside the recovery guards above, so
   * it cannot make a request that began during a recovery enter one.
   */
  readonly beforeRequest?: () => Promise<void>
}

/** The two codes {@link ApiClientConfiguration.recoverFromBuildIdentifierFailure} answers. */
const RECOVERABLE_BUILD_IDENTIFIER_CODES = new Set([
  'build_identifier_revoked',
  'build_identifier_invalid',
])

const BAD_REQUEST = 400

/**
 * Points the generated client at an API, and optionally gives it the credential and the
 * 401 recovery above. Call once, before the first request; calling it again re-points
 * every subsequent request.
 *
 * Passing a configuration without `headers` or `recoverFromUnauthorized` clears them,
 * which is what a sign-out or a test wants — this is configuration, not a subscription.
 */
export function configureApiClient(configuration: ApiClientConfiguration): void {
  configuredBaseUrl = configuration.baseUrl.replace(/\/+$/, '')
  configuredHeaders = configuration.headers
  configuredRecovery = configuration.recoverFromUnauthorized
  configuredBuildIdentifierRecovery = configuration.recoverFromBuildIdentifierFailure
  configuredBeforeRequest = configuration.beforeRequest
}

/** The configured origin, mostly so a consumer can show it or assert on it. */
export function apiBaseUrl(): string {
  return configuredBaseUrl
}

/**
 * Joins the configured origin to a generated path. The generated paths always begin
 * with `/`, and the base never ends with one, so exactly one slash separates them.
 */
export function resolveApiUrl(path: string): string {
  return `${configuredBaseUrl}${path.startsWith('/') ? path : `/${path}`}`
}

/**
 * What every generated endpoint function resolves to. The generated code names a
 * narrower version of this per endpoint — one member per documented status — and passes
 * it as the type argument below.
 */
export type ApiResponse<Data> = {
  data: Data
  status: number
  headers: Headers
}

/**
 * Performs the request and reads the body.
 *
 * A non-2xx is **not** thrown here: the generated types describe error responses as
 * ordinary results (`{ data: ApiError, status: 500 }`), and TanStack Query needs a
 * rejected promise to treat something as an error. Throwing is therefore the caller's
 * decision, made once in the query layer, and this function stays the transport.
 *
 * Statuses that carry no body by definition are not parsed, and a body that is not JSON
 * is surfaced as `undefined` data rather than as a parse exception — an error page from
 * a proxy should read as "status 502", not as a `SyntaxError` from somewhere in a hook.
 */
export async function apiFetch<Result extends ApiResponse<unknown>>(
  path: string,
  init?: RequestInit,
): Promise<Result> {
  // Read once, at the top: a request that *starts* while either recovery hook is
  // running is either that hook's own call or one issued during its window, and neither
  // may enter either recovery path below. This is the whole re-entrancy guard, and it is
  // a fact about when the request began rather than a list of endpoints (CF2). Both
  // recoveries share it: the config-file re-fetch below is itself an unauthenticated GET
  // that carries no credential and cannot 401, but a request issued *during* it is still
  // exempt from starting a second, concurrent recovery of its own.
  const startedDuringRecovery = unauthorizedRecovery.running || buildIdentifierRecovery.running
  // The second exemption, and the same shape of reasoning: the recovery repairs the
  // *configured* credential, so it can do nothing for a request that brought its own.
  // The replay re-sends this exact `init`, so it would present the token that was just
  // refused — and would have spent the single-use refresh token to get there, leaving
  // the caller holding a credential it never asked for and a failure it cannot fix. A
  // caller that pins a credential owns recovering it.
  const broughtOwnCredential = new Headers(init?.headers).has('authorization')
  const response = await send(path, init)

  if (!startedDuringRecovery) {
    if (response.status === UNAUTHORIZED && !broughtOwnCredential && configuredRecovery) {
      // Every request that was already in flight when the 401s began awaits this same
      // promise, so one expired access token produces one refresh.
      const recovered = await unauthorizedRecovery.run(configuredRecovery)
      // Exactly one replay. It cannot recurse: the flag is false again by now, and a
      // second 401 is the caller's to handle — which for this system means the session
      // has genuinely ended, not that another refresh is worth trying.
      return recovered ? ((await send(path, init)) as Result) : (response as Result)
    }

    if (
      response.status === BAD_REQUEST &&
      configuredBuildIdentifierRecovery &&
      RECOVERABLE_BUILD_IDENTIFIER_CODES.has(codeOf(response.data))
    ) {
      // One config-file re-fetch for concurrent revocation failures, exactly as above.
      const recovered = await buildIdentifierRecovery.run(configuredBuildIdentifierRecovery)
      // Exactly one replay. A second `build_identifier_revoked`/`_invalid` after a fresh
      // fetch is a real refusal — `docs/DECISIONS.md`, "The branding config file …" is
      // explicit this is not a loop.
      return recovered ? ((await send(path, init)) as Result) : (response as Result)
    }
  }

  return response as Result
}

const UNAUTHORIZED = 401

/** The `code` field of a parsed error body, or `''` when there isn't a usable one. */
function codeOf(data: unknown): string {
  const code = (data as { code?: unknown } | undefined)?.code
  return typeof code === 'string' ? code : ''
}

/**
 * One recovery hook's shared, de-duplicated in-flight state.
 *
 * `apiFetch` holds one of these per recovery kind (401, build-identifier) rather than one
 * shared pair, so a burst of *both* failure kinds at once produces at most one recovery
 * of each — never one blocking the other, and never one counted as the other's replay.
 */
class RecoveryState {
  /** True while this hook's own call is executing. See `apiFetch`'s re-entrancy guard. */
  running = false
  #inFlight: Promise<boolean> | undefined

  run(recovery: () => Promise<boolean>): Promise<boolean> {
    this.#inFlight ??= this.#execute(recovery)
    return this.#inFlight
  }

  async #execute(recovery: () => Promise<boolean>): Promise<boolean> {
    this.running = true
    try {
      return await recovery()
    } catch {
      // A hook that throws means "not recovered". It must never take the original
      // request's own failure handling with it.
      return false
    } finally {
      this.running = false
      this.#inFlight = undefined
    }
  }
}

const unauthorizedRecovery = new RecoveryState()
const buildIdentifierRecovery = new RecoveryState()

/** The transport proper: URL, configured headers, the request, and the body. */
async function send(path: string, init?: RequestInit): Promise<ApiResponse<unknown>> {
  // After this resolves the configured headers are read, so whatever the hook waited for
  // (the build identifier) is in them.
  // `if`, not `?.()`: an `await` of nothing still defers the request by a microtask, which moves every
  // consumer that has not opted in (the platform console's mount reads reach `fetch` after a test has swapped
  // its stub). Without a hook the request is sent in the same tick, exactly as before this existed.
  if (configuredBeforeRequest) await configuredBeforeRequest()
  const response = await fetch(resolveApiUrl(path), { ...init, headers: headersFor(init) })
  const body = BODYLESS_STATUSES.has(response.status) ? '' : await response.text()

  return { data: parseJson(body), status: response.status, headers: response.headers }
}

/**
 * The caller's headers, with the configured ones filled in where the caller left a gap.
 * The caller wins, so a request can still be made deliberately without a credential.
 */
function headersFor(init?: RequestInit): Headers {
  const headers = new Headers(init?.headers)

  for (const [name, value] of Object.entries(configuredHeaders?.() ?? {})) {
    if (!headers.has(name)) {
      headers.set(name, value)
    }
  }

  return headers
}

/** Statuses that are defined to carry no body. */
const BODYLESS_STATUSES = new Set([204, 205, 304])

function parseJson(body: string): unknown {
  if (body === '') {
    return undefined
  }
  try {
    return JSON.parse(body)
  } catch {
    return undefined
  }
}
